// Area (job page round): one section of a record page (Visits, Review, Invoice; Customer, Site), open or
// closed to one line. Its head is a button that opens and closes it (aria-expanded, a chevron that turns),
// its name and a count; closed, the line beside it says what's inside in a sentence, so a person can read
// the page without opening it; open, its actions sit at the end of the head. An area with nothing in it
// isn't drawn at all, or, given emptyLine, draws its head with that line and nothing to open.
// Areas are divided by a rule, never boxed (DESIGN.md §3). The chevron is the only motion, and none for
// prefers-reduced-motion. Reads only --area-*.
import { useId, useState, type ReactNode } from 'react';
import { Glyph } from '../Glyph/Glyph.tsx';
import './Area.css';

export interface AreaProps {
  /** Its name ("Visits"), the heading. */
  title: string;
  /** How many are in it, after the name, muted. */
  count?: number;
  /** Closed, the line beside the head: what's inside, in a sentence. Hidden while it's open. */
  summary?: ReactNode;
  /** Open, at the end of the head: Links ("Add a visit"). */
  actions?: ReactNode;
  /** What's in it; nothing means the area isn't drawn (or draws emptyLine). */
  children?: ReactNode;
  /** With nothing in it, the head is still drawn with this line ("No tasks"). */
  emptyLine?: string;
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The heading's level on the page (2). */
  level?: 2 | 3;
  /** An id for links that jump to it ("#a-history"). */
  id?: string;
}

/** The chevron an area's head draws; a record inside an area (FormRecord) draws the same one. */
export function AreaChevron({ open }: { open: boolean }) {
  return (
    <span className="fl-area__chev" data-open={open ? '' : undefined} aria-hidden="true">
      <Glyph name="chevron-right" className="fl-area__chev-glyph" />
    </span>
  );
}

export function Area({
  title,
  count,
  summary,
  actions,
  children,
  emptyLine,
  isOpen,
  defaultOpen = false,
  onOpenChange,
  level = 2,
  id,
}: AreaProps) {
  const [own, setOwn] = useState(defaultOpen);
  const open = isOpen ?? own;
  const uid = useId();
  const bodyId = `${uid}-body`;
  const headId = `${uid}-head`;
  const sumId = `${uid}-sum`;
  const empty = children === undefined || children === null || children === false;
  if (empty && !emptyLine) return null;

  const set = (o: boolean) => {
    if (isOpen === undefined) setOwn(o);
    onOpenChange?.(o);
  };
  const H = level === 2 ? 'h2' : 'h3';
  const name = (
    <>
      <span className="fl-area__name">{title}</span>
      {count !== undefined ? <span className="fl-area__count"> {count}</span> : null}
    </>
  );

  if (empty)
    return (
      <section id={id} className="fl-area" data-empty="" aria-labelledby={headId}>
        <div className="fl-area__head">
          <H id={headId} className="fl-area__title">
            <span className="fl-area__toggle" data-static="">
              <span className="fl-area__chev" aria-hidden="true" />
              {name}
            </span>
          </H>
          <span className="fl-area__sum">{emptyLine}</span>
          {actions ? <span className="fl-area__acts">{actions}</span> : null}
        </div>
      </section>
    );

  return (
    <section id={id} className="fl-area" data-open={open ? '' : undefined} aria-labelledby={headId}>
      <div className="fl-area__head">
        <H id={headId} className="fl-area__title">
          <button
            type="button"
            className="fl-area__toggle"
            aria-expanded={open}
            aria-controls={bodyId}
            aria-describedby={!open && summary ? sumId : undefined}
            onClick={() => set(!open)}
          >
            <AreaChevron open={open} />
            {name}
          </button>
        </H>
        {summary && !open ? (
          // the line opens it too, for the pointer; the keyboard has the button, which it describes
          <span id={sumId} className="fl-area__sum" onClick={() => set(true)}>
            {summary}
          </span>
        ) : null}
        {actions && open ? <span className="fl-area__acts">{actions}</span> : null}
      </div>
      <div id={bodyId} className="fl-area__body" hidden={!open}>
        {children}
      </div>
    </section>
  );
}
